/* Pulling frames out of a short video, in the browser.
 *
 * WHY THE BROWSER AND NOT THE CDN. ImageEngine will not touch an MP4 at all --
 * measured, ten different recipes including f_agif, every one answering
 * origin-object with the source bytes unchanged. GIF -> MP4 works (f_mp4) and
 * MP4 -> anything does not. The alternative was ffmpeg on the server, which is
 * a new binary, a large parser surface on untrusted input, and CPU on what is
 * still the Werkzeug development server.
 *
 * The browser is neither of those things and is fast: measured on a 480x270
 * clip in real Chrome, 24 frames in 120 ms -- five milliseconds a frame, every
 * frame carrying pixels.
 *
 * NOTHING HERE UPLOADS. The video is read off the device and never leaves it;
 * only the GIF made from it is ever submitted, through the ordinary intake.
 *
 * ONE FRAME AT A TIME, NEVER A LIST. onFrame() is awaited and the pixels are
 * handed on rather than collected: a hundred frames at 640x360 held as RGBA is
 * about 92 MB, which is fine on a laptop and is how you crash a phone. Every
 * function here that could return an array of frames deliberately does not.
 *
 * THE PURE HALF IS SEPARATE ON PURPOSE. planFrames(), fitWithin() and
 * estimateBytes() take numbers and return numbers, so tests/video_timing_test.mjs
 * can drive them in node. The decoding half cannot be tested that way, or in
 * any of the browser suites either -- see tests/video_to_gif_test.sh for why.
 */

/* The caps. Both are checked from the file's own metadata before a single
 * frame is decoded, and both are chosen from measured GIF weight rather than
 * from what the browser could manage.
 *
 * 640 rather than 1024 because a GIF is a bad container for video and the
 * output is what binds. Browser-made, photographic, 256-colour adaptive:
 *
 *     1024x576  12fps 10s   31.2 MB
 *      640x360  12fps 10s   14.5 MB
 *      480x270  10fps  5s    3.8 MB   <- the default, delivering 1.46 MB
 *
 * The upload ceiling is 50 MB (MAX_IMAGE_BYTES) and is NOT the constraint.
 * Usefulness is: nothing takes a 31 MB GIF. */
export const MAX_EDGE = 640
export const MAX_SECONDS = 10

export const DEFAULT_WIDTH = 480
export const DEFAULT_FPS = 10
export const MIN_FPS = 5
export const MAX_FPS = 15

/* How long to wait for one seek before giving up on the file.
 *
 * Generous because a phone decoding 4K is slow, and a decode that is merely
 * slow must not be reported as a file we cannot read. */
export const SEEK_TIMEOUT_MS = 8000

/* Fitted to four measurements of browser-made GIFs on photographic content:
 *
 *     320x180   0.678 bytes per pixel per frame
 *     480x270   0.584
 *     640x360   0.524
 *    1024x576   0.441
 *
 * bytes/px/frame = 5.12 * px^-0.1845, which reproduces all four to within a
 * thousandth. It is an ESTIMATE and says so wherever it is shown: flat cartoon
 * frames compress far better than this and a noisy photograph slightly worse.
 * It exists so the size can be shown while the controls move, not to promise a
 * number. */
export function bytesPerPixelPerFrame(pixels) {
    if (!(pixels > 0)) return 0
    return 5.12 * Math.pow(pixels, -0.1845)
}

export function estimateBytes(width, height, frames) {
    const px = Math.max(0, Math.round(width)) * Math.max(0, Math.round(height))
    if (!px || !frames) return 0
    return Math.round(px * frames * bytesPerPixelPerFrame(px))
}

/* What the CDN will hand back afterwards.
 *
 * Measured: a 3,786,213-byte browser-made GIF came back 1,461,334 at w_432,
 * which is the width /mp4-to-gif's pct 90 asks for. 0.386 of what went up. */
export const DELIVERED_SHARE = 0.386

export function estimateDelivered(width, height, frames) {
    return Math.round(estimateBytes(width, height, frames) * DELIVERED_SHARE)
}

/* Fit a source size inside a target width, never enlarging it.
 *
 * Never enlarging matters twice over: upscaling a video wastes bytes on
 * invented pixels, and asking the CDN for more than the source width is what
 * makes it answer origin-object and hand the file straight back.
 *
 * Both edges are held to MAX_EDGE, so a portrait clip from a phone is capped
 * on its height rather than sailing through at 640x1138. */
export function fitWithin(srcW, srcH, targetW, maxEdge = MAX_EDGE) {
    const w0 = Math.max(1, Math.round(srcW || 0))
    const h0 = Math.max(1, Math.round(srcH || 0))
    if (!srcW || !srcH) return {width: 0, height: 0}

    let w = Math.min(w0, Math.max(1, Math.round(targetW || w0)), maxEdge)
    let h = Math.max(1, Math.round(w * h0 / w0))
    if (h > maxEdge) {
        h = maxEdge
        w = Math.max(1, Math.round(h * w0 / h0))
    }
    // Even dimensions are not required by GIF, but an odd width on a scaled
    // canvas gives the last column a half pixel of its neighbour.
    return {width: w, height: h}
}

/* Which moments in the clip become frames, and how long each is held.
 *
 * A RANGE, not a prefix. The visitor picks which part of the clip becomes the
 * animation, so this takes `start` and `end` in source seconds.
 *
 * THE CAP IS ON THE GIF, NOT ON THE SELECTION. What costs bytes is the number
 * of frames, and that is `playSeconds * fps` however wide a span they came
 * from -- measured: 100 frames at 480x270 is about 7.6 MB whether it spans ten
 * seconds or sixty. So a selection longer than MAX_SECONDS is not refused and
 * not trimmed; it is SAMPLED across the whole span and played at the same
 * frame rate, which comes out as a speed-up. Thirteen seconds becomes ten at
 * 1.3x, thirty becomes ten at 3x.
 *
 * That replaces the old behaviour, which used the first ten seconds and said
 * so. Nothing is discarded now unless the visitor discards it themselves with
 * the handles, and `speed` is what the notice reports instead.
 *
 * The delay is stored in the GIF in HUNDREDTHS of a second, not milliseconds,
 * so a frame rate that does not divide 100 cannot be represented exactly: 12
 * fps is 8.33 hundredths and becomes 8, which plays at 12.5 fps. The delay is
 * therefore computed ONCE here and used both to place the frames and to write
 * them, so the timing that was planned is the timing that is encoded. Planning
 * in milliseconds and rounding at the encoder is how a clip comes out running
 * at a different speed from its preview -- and now that a deliberate speed-up
 * exists, an accidental one would be indistinguishable from it.
 *
 * No frame is asked for at the very end of the span: seeking exactly to
 * `duration` returns nothing on some builds, which cost a whole run once.
 */
/* The delay one frame is held for, in HUNDREDTHS -- the unit the GIF stores.
 *
 * Exported because the encoder has to be built with it before the first frame
 * arrives, and the frame placement has to use the same number. Two spellings
 * of `round(100 / fps)` is how the encoded running time comes to disagree with
 * the one the trimmer showed -- and now that a deliberate speed-up exists, an
 * accidental one would look exactly like it.
 *
 * Floored at 2 because a GIF delay of 0 or 1 hundredth is played at whatever
 * speed the viewer feels like, which is not a frame rate at all. */
export function delayForFps(fps) {
    const rate = Math.min(MAX_FPS, Math.max(MIN_FPS, Math.round(fps || DEFAULT_FPS)))
    return {rate, delayCs: Math.max(2, Math.round(100 / rate))}
}

export function planFrames(duration, fps, maxSeconds = MAX_SECONDS, start = 0, end = null) {
    const d = Number(duration)
    const empty = {times: [], delayCs: 0, delayMs: 0, seconds: 0, span: 0,
                   speed: 1, sped: false, from: 0, to: 0, fps: 0}
    if (!(d > 0)) return empty

    const {rate, delayCs} = delayForFps(fps)

    const from = Math.min(Math.max(0, Number(start) || 0), d)
    const to = Math.min(Math.max(from, end === null || end === undefined ? d : Number(end) || 0), d)
    const span = to - from
    if (!(span > 0)) return empty

    // How long the ANIMATION runs. The selection may be longer.
    const cap = Math.max(0, Number(maxSeconds) || 0)
    const playSeconds = cap ? Math.min(span, cap) : span

    /* Frames are whole and the delay is whole hundredths, so the running time
     * can only land on a multiple of the delay. ROUNDED rather than floored:
     * flooring always loses up to one frame, which made a 4.3-second selection
     * come out 4.2 seconds long -- a 2% speed-up that the notice then reported
     * as "1x faster than the original". Rounding costs at most half a frame
     * and keeps a selection inside the cap at its own speed. */
    const count = Math.max(1, Math.round((playSeconds * 100) / delayCs))
    const step = count > 1 ? span / count : 0

    const times = []
    for (let i = 0; i < count; i++) {
        times.push(Math.min(from + i * step, Math.max(0, to - 1e-3)))
    }

    const played = (count * delayCs) / 100
    const speed = played > 0 ? span / played : 1

    return {
        times, delayCs, delayMs: delayCs * 10, fps: rate,
        span, seconds: played, playSeconds: played,
        /* Only call it sped up when it is worth saying. Anything under a
         * twentieth is the rounding above, not a decision, and a notice that
         * announces "1x faster" is worse than no notice. */
        speed, sped: speed > 1.05,
        from, to,
    }
}

/* A <video> HAS TO BE IN THE DOCUMENT, on iOS.
 *
 * Both readers below created an element with document.createElement and never
 * attached it. Every desktop browser is happy with that -- measured, Chrome
 * loads, seeks and draws from a detached element without complaint, which is
 * exactly why this survived so long and why no suite here caught it.
 *
 * iOS Safari is not. It will not load media for an element outside the
 * document, and it treats preload as "none" until a user gesture -- and the
 * tap that opened the photo picker is spent by the time the File comes back
 * from it. The result is the worst possible shape: `loadedmetadata` FIRES,
 * the duration is real, and videoWidth is 0 -- which is indistinguishable
 * from the HEVC case this file already documents, and was reported as it.
 *
 * Reported from an iPhone on a 4K .mov that is ORDINARY H.264 and converts
 * here in thirteen seconds: 48,734,471 bytes, avc1, 2160x3840, 16.8s. So the
 * codec was never the problem and neither was the size.
 *
 * Off-screen rather than hidden: display:none and visibility:hidden are both
 * grounds for iOS to decline to decode, and a 1px element in the corner is
 * not. It is removed again in the same cleanup that revokes the object URL.
 *
 * NOT VERIFIED ON A DEVICE. There is no iPhone here; this is the documented
 * requirement and it matches every symptom, but the confirmation has to come
 * from the phone that reported it.
 */
function attach(video) {
    video.style.cssText =
        "position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0;pointer-events:none"
    video.setAttribute("aria-hidden", "true")
    try { document.body.appendChild(video) } catch (e) { /* no body yet: detached is what we had */ }
}

function detach(video) {
    try { if (video.parentNode) video.parentNode.removeChild(video) } catch (e) { /* already gone */ }
}

/* A row of thumbnails for the trimmer, and the clip's own length.
 *
 * Separate from decodeFrames() rather than a mode of it: this wants a handful
 * of tiny pictures as data URLs to put in <img> tags, and that one wants every
 * frame as ImageData handed straight to an encoder and released. Sharing them
 * would mean a function that returns two incompatible things.
 *
 * Cheap by design -- ten seeks at 64 pixels tall. It is also where the
 * duration comes from, which the trimmer needs before it can draw anything.
 */
export const STRIP_COUNT = 10
export const STRIP_HEIGHT = 64

export async function sampleStrip(file, opts) {
    const {count = STRIP_COUNT, height = STRIP_HEIGHT, signal = null} = opts || {}
    if (typeof document === "undefined") throw new Error("no document")

    const url = URL.createObjectURL(file)
    const video = document.createElement("video")
    video.muted = true
    video.defaultMuted = true
    video.playsInline = true
    video.setAttribute("playsinline", "")
    video.setAttribute("muted", "")
    video.preload = "auto"
    attach(video)

    try {
        await new Promise((resolve, reject) => {
            video.addEventListener("loadedmetadata", resolve, {once: true})
            video.addEventListener("error", () => {
                const code = video.error && video.error.code
                reject(new Error("undecodable" + (code ? " (" + code + ")" : "")))
            }, {once: true})
            setTimeout(() => reject(new Error("metadata timed out")), SEEK_TIMEOUT_MS)
            video.src = url
            /* EXPLICIT, because iOS does not reliably start on assignment
             * alone. Setting .src queues a load on every other browser; on
             * iOS Safari the element can sit at readyState 0 until asked. A
             * second load() elsewhere is harmless -- it restarts the same
             * fetch of a blob: URL that is already in memory. */
            try { video.load() } catch (e) { /* the src assignment stands */ }
        })

        const srcW = video.videoWidth
        const srcH = video.videoHeight
        const duration = video.duration
        /* 0x0 WITH A VALID DURATION IS AN UNSUPPORTED VIDEO CODEC.
         *
         * `loadedmetadata` fires, and the duration is real, because the AUDIO
         * track decoded -- the video track did not, so there are no
         * dimensions. Measured on a phone recording: hvc1 (HEVC) 1920x1080
         * with an mp4a track, duration 3.56s, videoWidth 0.
         *
         * It reads as a broken file and is nothing of the kind: the phone that
         * recorded it plays it perfectly. So it is reported as "this browser
         * cannot read it", which is what video.undecodable says. */
        if (!srcW || !srcH) throw new Error("undecodable: no video track this browser can read")
        if (!(duration > 0) || !isFinite(duration)) throw new Error("no length in this file")

        const w = Math.max(1, Math.round(height * srcW / srcH))
        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = height
        const ctx = canvas.getContext("2d")
        if (!ctx) throw new Error("this browser has no canvas to work on")

        const thumbs = []
        for (let i = 0; i < count; i++) {
            if (signal && signal.aborted) throw new Error("cancelled")
            // Sampled at the MIDDLE of each slice, so the first thumbnail is
            // not the black frame a great many clips open on.
            const at = Math.min(duration * (i + 0.5) / count, Math.max(0, duration - 1e-3))
            const settled = await seekTo(video, at)
            if (!settled) {
                if (!thumbs.length) throw new Error("undecodable")
                break
            }
            ctx.drawImage(video, 0, 0, w, height)
            thumbs.push(canvas.toDataURL("image/jpeg", 0.6))
        }

        return {thumbs, duration, width: srcW, height: srcH,
                thumbWidth: w, thumbHeight: height}
    } finally {
        try { video.removeAttribute("src"); video.load() } catch (e) { /* nothing to undo */ }
        detach(video)
        URL.revokeObjectURL(url)
    }
}

/* Is this file something we are willing to try?
 *
 * Deliberately permissive about the container. An iPhone records HEVC in a
 * .mov by default and Safari decodes it natively, so refusing on the extension
 * would turn away the commonest video anybody has -- while desktop Chrome may
 * well fail on the same file. Which of those is happening cannot be known
 * without asking the browser, so the answer is to try and to report a decode
 * failure in words rather than to guess from a filename. */
const VIDEO_EXT = /\.(mp4|m4v|mov|webm|ogv|ogg|avi|mkv|3gp)$/i

export function looksLikeVideo(file) {
    if (!file) return false
    const type = (file.type || "").toLowerCase()
    if (type.startsWith("video/")) return true
    // Some pickers hand over an empty type for a .mov; the name is all there is.
    if (!type && VIDEO_EXT.test(file.name || "")) return true
    return false
}

/* Decode the clip, handing each frame on as it arrives.
 *
 * onFrame(imageData, index, total) may be async and IS awaited, which is what
 * keeps one frame in memory rather than all of them.
 *
 * Seeking rather than playing. Playing and taking frames from
 * requestVideoFrameCallback delivers whatever the compositor felt like showing
 * -- fine for a preview, wrong for a fixed frame rate, and it cannot be made
 * to run faster than real time. Seeking gives exactly the moments planFrames()
 * asked for.
 */
export async function decodeFrames(file, opts, onFrame) {
    const {
        targetWidth = DEFAULT_WIDTH,
        fps = DEFAULT_FPS,
        maxSeconds = MAX_SECONDS,
        maxEdge = MAX_EDGE,
        start = 0,
        end = null,
        signal = null,
    } = opts || {}

    if (typeof document === "undefined") throw new Error("no document")

    const url = URL.createObjectURL(file)
    const video = document.createElement("video")
    // Both are REQUIRED on iOS before a video will decode without a gesture,
    // and playsInline stops Safari taking the frame fullscreen.
    video.muted = true
    video.defaultMuted = true
    video.playsInline = true
    video.setAttribute("playsinline", "")
    video.setAttribute("muted", "")
    video.preload = "auto"
    video.crossOrigin = "anonymous"
    attach(video)

    const cleanup = () => {
        try { video.removeAttribute("src"); video.load() } catch (e) { /* nothing to undo */ }
        detach(video)
        URL.revokeObjectURL(url)
    }

    try {
        await new Promise((resolve, reject) => {
            const fail = () => {
                const code = video.error && video.error.code
                reject(new Error("undecodable" + (code ? " (" + code + ")" : "")))
            }
            video.addEventListener("loadedmetadata", resolve, {once: true})
            video.addEventListener("error", fail, {once: true})
            setTimeout(() => reject(new Error("metadata timed out")), SEEK_TIMEOUT_MS)
            video.src = url
            /* EXPLICIT, because iOS does not reliably start on assignment
             * alone. Setting .src queues a load on every other browser; on
             * iOS Safari the element can sit at readyState 0 until asked. A
             * second load() elsewhere is harmless -- it restarts the same
             * fetch of a blob: URL that is already in memory. */
            try { video.load() } catch (e) { /* the src assignment stands */ }
        })

        const srcW = video.videoWidth
        const srcH = video.videoHeight
        const duration = video.duration
        // Same as sampleStrip(): 0x0 with a real duration means the audio
        // track decoded and the video track did not.
        if (!srcW || !srcH) throw new Error("undecodable: no video track this browser can read")
        if (!(duration > 0) || !isFinite(duration)) throw new Error("no length in this file")

        const size = fitWithin(srcW, srcH, targetWidth, maxEdge)
        const plan = planFrames(duration, fps, maxSeconds, start, end)

        const canvas = document.createElement("canvas")
        canvas.width = size.width
        canvas.height = size.height
        const ctx = canvas.getContext("2d", {willReadFrequently: true})
        if (!ctx) throw new Error("this browser has no canvas to work on")

        let drawn = 0
        for (let i = 0; i < plan.times.length; i++) {
            if (signal && signal.aborted) throw new Error("cancelled")

            const settled = await seekTo(video, plan.times[i])
            if (!settled) {
                // A file that decodes its first frames and then stalls is worse
                // than one that never started: stopping with what we have makes
                // a short GIF out of a long clip and says nothing. Only a total
                // failure is silent-safe, and that is reported below.
                if (drawn === 0) throw new Error("undecodable")
                throw new Error("stalled after " + drawn + " frames")
            }
            ctx.drawImage(video, 0, 0, size.width, size.height)
            await onFrame(ctx.getImageData(0, 0, size.width, size.height), i, plan.times.length)
            drawn++
        }

        return {
            width: size.width, height: size.height,
            frames: drawn, delayCs: plan.delayCs, fps: plan.fps,
            seconds: plan.seconds, span: plan.span,
            speed: plan.speed, sped: plan.sped,
            from: plan.from, to: plan.to,
            sourceWidth: srcW, sourceHeight: srcH, duration,
        }
    } finally {
        cleanup()
    }
}

function seekTo(video, time) {
    return new Promise((resolve) => {
        let done = false
        const finish = (ok) => {
            if (done) return
            done = true
            video.removeEventListener("seeked", onSeeked)
            resolve(ok)
        }
        const onSeeked = () => finish(true)
        video.addEventListener("seeked", onSeeked, {once: true})
        setTimeout(() => finish(false), SEEK_TIMEOUT_MS)
        try {
            video.currentTime = time
        } catch (e) {
            finish(false)
        }
    })
}

/* TEN THUMBNAILS WITH NO VIDEO, for #selftest-video.
 *
 * tools/shots.js has to photograph the trimmer, and the trimmer needs a
 * filmstrip. It cannot get one the real way: sampleStrip() seeks a <video>,
 * and the media pipeline does not survive --virtual-time-budget at all
 * (measured in tests/video_to_gif_test.sh -- one frame in 4,024 ms). So the
 * strip is drawn instead of sampled.
 *
 * It lives HERE and not in App.vue for one reason: tests/test_palette.py
 * refuses a raw hex literal in a .vue file, and it is right to -- a colour
 * written into a component is a colour that never went through the palette.
 * These four are a picture rather than an interface, the same argument
 * lib/starters.js makes for the blank meme cards, and a .js module is where
 * that argument is allowed.
 *
 * A PAN, so the ten differ from one another. Ten identical rectangles would
 * make the screenshot useless for explaining what a filmstrip is.
 */
const DEMO_SKY = ["#1d3f6e", "#7fa8d4", "#e9c9a0"]
const DEMO_HILL = "#2c3a2a"

export function demoStrip(count = 10, w = 96, h = 54) {
    const out = []
    for (let i = 0; i < count; i++) {
        const c = document.createElement("canvas")
        c.width = w; c.height = h
        const x = c.getContext("2d")
        const sky = x.createLinearGradient(0, 0, 0, h)
        sky.addColorStop(0, DEMO_SKY[0])
        sky.addColorStop(0.55, DEMO_SKY[1])
        sky.addColorStop(1, DEMO_SKY[2])
        x.fillStyle = sky
        x.fillRect(0, 0, w, h)
        x.fillStyle = DEMO_HILL
        x.beginPath()
        x.moveTo(0, h)
        for (let px = 0; px <= w; px += 6) {
            x.lineTo(px, h * 0.7 + 6 * Math.sin((px + i * 9) / 14))
        }
        x.lineTo(w, h)
        x.closePath()
        x.fill()
        out.push(c.toDataURL("image/jpeg", 0.7))
    }
    return out
}
